/* Live class subject toolkit drawer.
 *
 * Teacher-only side panel shown inside `/live/:classId`. Lists every asset linked to
 * the class's subject (chapters, quizzes, animated stories, library, past papers).
 * Clicking "Share with class" calls POST /api/live/{classId}/share which sets the
 * shared overlay state. Students poll it every couple seconds and render the same
 * thing in their view.
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  X, BookOpen, ClipboardList, ScrollText, Library as LibraryIcon, FileText,
  Share2, Loader2, Sparkles, ChevronRight, Hash, Crown, Layers as LayersIcon,
  Wand2, Eye, Image as ImageIcon,
} from "lucide-react";
import { correctIndexOf } from "@/lib/quizUtils";
import SubjectPicker from "@/components/whiteboard/SubjectPicker";
import InlineCreateModal from "@/components/live/InlineCreateModal";
import ShareTargetModal from "@/components/live/ShareTargetModal";

const TABS = [
  { id: "chapters", label: "Chapters", icon: BookOpen, color: "#0EA5E9" },
  { id: "quizzes", label: "Quizzes", icon: ClipboardList, color: "#7C3AED" },
  { id: "games", label: "Games", icon: Crown, color: "#FFD600" },
  { id: "stories", label: "Stories", icon: ScrollText, color: "#F97316" },
  { id: "slide_decks", label: "Slides", icon: LayersIcon, color: "#0891B2" },
  { id: "library", label: "Library", icon: LibraryIcon, color: "#A855F7" },
];

// Quick-create shortcuts inside the live drawer. Each one opens an in-drawer
// modal so the teacher never leaves the room — the video / chat / whiteboard
// stay live in the background.
const CREATE_ACTIONS = [
  { key: "quiz",    label: "Quiz",    icon: ClipboardList },
  { key: "story",   label: "Story",   icon: ScrollText    },
  { key: "slides",  label: "Slides",  icon: LayersIcon    },
  { key: "game",    label: "Game",    icon: Crown         },
  { key: "diagram", label: "Diagram", icon: ImageIcon     },
];

export default function LiveSubjectDrawer({ open, onClose, classId, onShared }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("chapters");
  const [sharing, setSharing] = useState(null);
  const [cls, setCls] = useState(null); // full class doc so we know current subject_id
  const [linking, setLinking] = useState(false);
  const [liveResponses, setLiveResponses] = useState([]);
  const [createKind, setCreateKind] = useState(null); // "quiz" | "story" | "slides" | null
  const [pendingShare, setPendingShare] = useState(null); // interactive share awaiting target pick

  // Load the class once we open, so the SubjectPicker knows the current selection
  useEffect(() => {
    if (!open || !classId) return;
    api.get(`/classes/${classId}`).then(({ data }) => setCls(data.class)).catch(() => {});
  }, [open, classId]);

  // Poll live quiz responses every 3s while the drawer is open so the teacher
  // can watch students answering in real time.
  useEffect(() => {
    if (!open || !classId) return;
    let stopped = false;
    const pull = async () => {
      try {
        const { data } = await api.get(`/live/${classId}/quiz-responses`);
        if (!stopped) setLiveResponses(data.responses || []);
      } catch { /* teacher may not yet be authorised, ignore */ }
    };
    pull();
    const t = setInterval(pull, 3000);
    return () => { stopped = true; clearInterval(t); };
  }, [open, classId]);

  // Prefill the AI creation page with the class subject so the teacher doesn't
  // have to pick it again. Opens an INLINE modal on top of the drawer so the
  // teacher never leaves the video room while the asset is being built.
  const openCreate = (kind) => {
    // Diagram is topic-only and doesn't need a subject linked; every other
    // asset type is scoped to the class subject.
    if (kind !== "diagram" && !cls?.subject_id) {
      toast.error("Link a subject to this class first (top of drawer)");
      return;
    }
    setCreateKind(kind);
  };

  // When a new asset finishes generating, inject it into the drawer's data
  // and jump the user to that tab so they see the freshly-made item at top.
  const injectCreated = (listKey, asset) => {
    if (!asset) return;
    setData((d) => {
      if (!d) return d;
      const list = d[listKey] || [];
      return { ...d, [listKey]: [asset, ...list.filter((x) => x.id !== asset.id)] };
    });
    // Map the drawer's list key back to the tab id used by the tab strip.
    const tabId = listKey === "slide_decks" ? "slide_decks" : listKey;
    setTab(tabId);
  };

  // "Share live now" from the modal success step — bypasses the drawer list
  // and pushes the asset overlay directly to students.
  const shareFromModal = (listKey, asset) => {
    if (!asset) return;
    if (listKey === "quizzes") {
      share("quiz", asset.id, { title: asset.title, category: asset.category || "mcq", questions: asset.questions || [] });
    } else if (listKey === "stories") {
      share("story", asset.id, { title: asset.title, slides: asset.slides || [] });
    } else if (listKey === "slide_decks") {
      share("slides", asset.id, { title: asset.title, theme: asset.theme, slides: asset.slides || [] });
    } else if (listKey === "games") {
      share("millionaire", asset.id, { title: asset.title, questions: asset.questions || [] });
    } else if (listKey === "library") {
      // Diagram (image) — push as an inline image overlay via the resource type.
      // The overlay renderer inlines images when kind === "image".
      share("resource", asset.id || `diagram-${Date.now()}`, {
        title: asset.title || "Diagram",
        file_url: asset.url || (asset.data ? `data:image/png;base64,${asset.data}` : ""),
        kind: "image",
      });
    }
  };

  const linkSubject = async (subjectId) => {
    setLinking(true);
    try {
      const { data: r } = await api.patch(`/classes/${classId}`, { subject_id: subjectId });
      setCls(r.class);
      toast.success(r.class.subject_name ? `Linked · ${r.class.subject_name}` : "Subject cleared");
      // Refresh scoped assets
      setData(null);
      setLoading(true);
      try {
        const [scoped, all] = await Promise.allSettled([
          api.get(`/live/${classId}/subject-assets`),
          api.get(`/live/teacher-assets`),
        ]);
        const s = scoped.status === "fulfilled" ? (scoped.value.data || {}) : {};
        const a = all.status === "fulfilled" ? (all.value.data || {}) : {};
        const hasSubject = !!(s.subject_id);
        const pick = (subjectList, allList) => hasSubject ? (subjectList || []) : (allList || subjectList || []);
        setData({
          subject_id: s.subject_id || "",
          subject_name: s.subject_name || "",
          chapters:    s.chapters || [],
          quizzes:     pick(s.quizzes,     a.quizzes),
          games:       pick(s.games,       a.games),
          stories:     pick(s.stories,     a.stories),
          slide_decks: pick(s.slide_decks, a.decks),
          library:     s.library || [],
        });
      } finally { setLoading(false); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not link subject");
    } finally { setLinking(false); }
  };

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        // Load subject-scoped assets AND the full teacher-assets rollup in parallel.
        // Then merge (dedupe by id) so the drawer always shows every teachable asset,
        // even when the class isn't linked to a subject (A20).
        const [scoped, all] = await Promise.allSettled([
          api.get(`/live/${classId}/subject-assets`),
          api.get(`/live/teacher-assets`),
        ]);
        if (!mounted) return;
        const s = scoped.status === "fulfilled" ? (scoped.value.data || {}) : {};
        const a = all.status === "fulfilled" ? (all.value.data || {}) : {};
        const hasSubject = !!(s.subject_id);
        // If a subject IS linked, ONLY show scoped assets (no cross-contamination).
        // If no subject is linked, fall back to the full teacher rollup.
        const pick = (subjectList, allList) => hasSubject ? (subjectList || []) : (allList || subjectList || []);
        setData({
          subject_id: s.subject_id || "",
          subject_name: s.subject_name || "",
          chapters:    s.chapters || [],
          quizzes:     pick(s.quizzes,     a.quizzes),
          games:       pick(s.games,       a.games),
          stories:     pick(s.stories,     a.stories),
          slide_decks: pick(s.slide_decks, a.decks),
          library:     s.library || [],
        });
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not load subject assets");
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [open, classId]);

  // Interactive activities (quiz / game) first ask WHO gets to play — everyone
  // watches, only the chosen participants get control. Passive content
  // (chapters, stories, slides, resources) shares straight to everyone.
  const share = async (type, ref_id, payload) => {
    // Every share type asks WHO first (everyone vs specific students).
    setPendingShare({ type, ref_id, payload });
  };

  const doShare = async (type, ref_id, payload, allowed_ids, allowed_names) => {
    setSharing(`${type}-${ref_id}`);
    try {
      const { data } = await api.post(`/live/${classId}/share`, { type, ref_id, payload, allowed_ids, allowed_names });
      toast.success(allowed_ids.includes("*") ? "Shared with class" : `Shared · ${allowed_names.length} participant${allowed_names.length === 1 ? "" : "s"} can play`);
      onShared?.(data.overlay);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Share failed");
    } finally { setSharing(null); }
  };

  if (!open) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[85] bg-black/60 backdrop-blur-sm flex justify-end"
        onClick={onClose}
        data-testid="live-subject-drawer"
      >
        <motion.aside
          initial={{ x: 400 }} animate={{ x: 0 }} exit={{ x: 400 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="w-full max-w-md h-full bg-[#10141C] border-l border-white/10 overflow-visible flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-5 border-b border-white/10">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#FFD600] font-bold">/ subject toolkit</div>
                <h2 className="font-display font-bold text-2xl tracking-tighter text-white mt-1 truncate">
                  {loading && !data ? "Loading…" : (cls?.subject_name || "All subjects")}
                </h2>
                <p className="text-xs text-white/50 mt-1">Pick anything below to push it live to your students.</p>
              </div>
              <button onClick={onClose} className="ml-3 w-9 h-9 rounded-full border border-white/15 text-white/60 hover:text-white hover:border-[#22D3EE]/60 hover:bg-[#22D3EE]/10 hover:shadow-[0_0_14px_rgba(34,211,238,0.4)] inline-flex items-center justify-center transition-all duration-200 hover:scale-105" data-testid="drawer-close"><X size={16} /></button>
            </div>
            {/* Subject picker — teacher can pick/switch/add subject live */}
            <div className="mt-4 flex items-center gap-2" data-testid="live-subject-linker">
              <span className="text-[10px] font-mono uppercase tracking-wider text-white/50 shrink-0">Subject:</span>
              <div className="flex-1">
                <SubjectPicker
                  value={cls?.subject_id || ""}
                  onChange={(id) => linkSubject(id)}
                  buttonClassName="w-full !bg-white/5 !border-white/15 hover:!border-white/40 !text-white"
                />
              </div>
              {linking && <Loader2 size={12} className="animate-spin text-white/60" />}
            </div>
            {/* tabs */}
            <div className="flex gap-1.5 mt-5 flex-wrap">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.id;
                const count = (data?.[t.id] || []).length;
                return (
                  <button
                    key={t.id}
                    onClick={() => setTab(t.id)}
                    data-testid={`drawer-tab-${t.id}`}
                    className={`flex items-center gap-2 px-3.5 py-2.5 text-[12px] font-semibold rounded-full border transition-all duration-200 hover:scale-105 ${active
                      ? "text-[#05070C]"
                      : "border-white/10 text-white/60 hover:text-white hover:border-white/40 bg-white/[.03]"}`}
                    style={active ? { background: t.color, borderColor: t.color, boxShadow: `0 0 16px ${t.color}66` } : undefined}
                  >
                    <Icon size={14} strokeWidth={1.8} style={!active ? { color: t.color } : undefined} />
                    {t.label}
                    <span className="text-[10px] font-mono opacity-70">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-5 space-y-3">
            {/* Quick-create actions — teacher can spin up any asset without leaving live class */}
            <div className="border border-white/10 rounded-lg p-3 bg-white/[.03]" data-testid="drawer-quick-create">
              <div className="flex items-center gap-1.5 mb-2">
                <Wand2 size={12} className="text-[#FFD600]" />
                <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/60 font-bold">Create new · pre-filled with this subject</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {CREATE_ACTIONS.map((a) => (
                  <button key={a.key} onClick={() => openCreate(a.key)} data-testid={`drawer-create-${a.key}`}
                    className="border border-white/10 hover:border-[#FFD600] hover:bg-[#FFD600]/15 hover:shadow-[0_0_16px_rgba(255,214,0,0.35)] hover:scale-105 rounded-xl px-3 py-3 text-white/80 hover:text-white transition-all duration-200 inline-flex items-center gap-2.5">
                    <span className="w-8 h-8 rounded-full bg-[#FFD600]/15 border border-[#FFD600]/30 inline-flex items-center justify-center shrink-0">
                      <a.icon size={15} className="text-[#FFD600]" />
                    </span>
                    <span className="text-[12px] font-bold leading-none">{a.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Live student activity */}
            {liveResponses.length > 0 && (
              <div className="border border-[#10B981]/30 rounded-lg p-3 bg-[#10B981]/5" data-testid="drawer-live-responses">
                <div className="flex items-center gap-1.5 mb-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
                  <Eye size={11} className="text-[#10B981]" />
                  <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#10B981] font-bold">Live · {liveResponses.length} responding</span>
                </div>
                <div className="space-y-1 max-h-[140px] overflow-y-auto">
                  {liveResponses.slice(0, 10).map((r, i) => (
                    <div key={`${r.student_id}-${r.quiz_id}-${i}`} className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-white/[.03]" data-testid={`drawer-resp-${r.student_id}`}>
                      <div>
                        <div className="text-white font-semibold">{r.student_name}</div>
                        <div className="text-[10px] font-mono text-white/40">{r.status} · {Object.keys(r.answers || {}).length} answered</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[13px] font-bold" style={{ color: r.score / Math.max(1, r.total) >= 0.6 ? "#10B981" : "#F59E0B" }}>
                          {r.score}/{r.total}
                        </div>
                        <div className="text-[9px] font-mono text-white/40">{r.time_s}s</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {loading || !data ? (
              <div className="text-center py-16">
                <Loader2 size={22} className="animate-spin text-white/40 mx-auto mb-3" />
                <div className="text-xs text-white/40 font-mono uppercase tracking-wider">Loading subject…</div>
              </div>
            ) : tab === "chapters" ? (
              <ChaptersList items={data.chapters} onShare={(c) => share("chapter", c.id, { title: c.title, pages: c.pages || [] })} sharing={sharing} />
            ) : tab === "quizzes" ? (
              <QuizzesList items={data.quizzes} onShare={(q) => share("quiz", q.id, { title: q.title, category: q.category || "mcq", questions: q.questions || [] })} sharing={sharing} />
            ) : tab === "stories" ? (
              <StoriesList items={data.stories} onShare={(s) => share("story", s.id, { title: s.title, slides: s.slides || [] })} sharing={sharing} />
            ) : tab === "games" ? (
              <GamesList items={data.games} onShare={(g) => share("millionaire", g.id, { title: g.title, questions: g.questions || [] })} sharing={sharing} />
            ) : tab === "slide_decks" ? (
              <SlideDecksList items={data.slide_decks} onShare={(d) => share("slides", d.id, { title: d.title, theme: d.theme, slides: d.slides || [] })} sharing={sharing} />
            ) : tab === "library" ? (
              <LibraryList items={data.library} onShare={(a) => share("resource", a.id, { title: a.title, file_url: a.file_url, kind: a.kind })} sharing={sharing} />
            ) : null}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-white/10 text-[11px] text-white/40 font-mono uppercase tracking-[0.18em] flex items-center gap-2">
            <Sparkles size={11} className="text-[#FFD600]" /> Students see what you share, instantly.
          </div>
        </motion.aside>
      </motion.div>

      {/* In-drawer generator — modal keeps the teacher inside the room */}
      <InlineCreateModal
        open={!!createKind}
        kind={createKind}
        onClose={() => setCreateKind(null)}
        subject={{ id: cls?.subject_id, name: cls?.subject_name, grade: cls?.grade_name || cls?.grade }}
        onCreated={injectCreated}
        onShareLive={shareFromModal}
      />

      {/* Who-gets-control picker for interactive shares (quiz / game) */}
      <ShareTargetModal
        open={!!pendingShare}
        classId={classId}
        title={pendingShare?.type === "millionaire" ? "Share this game" : pendingShare?.type === "slides" ? "Present these slides" : pendingShare?.type === "quiz" ? "Share this quiz" : `Share this ${pendingShare?.type || "item"}`}
        subtitle={`Everyone in class will see ${pendingShare?.type === "slides" ? "your slides" : pendingShare?.type === "millionaire" ? "the game" : pendingShare?.type === "quiz" ? "the quiz" : "it"} — choose the sharing mode.`}
        everyoneDesc={pendingShare?.type === "slides" ? "You present — everyone follows your navigation" : pendingShare?.type === "quiz" || pendingShare?.type === "millionaire" ? "Each participant attempts independently — you see a live progress list" : "Broadcast to the whole class — everyone watches"}
        specificDesc="Remote access — they control ONE shared screen; everyone (incl. you) watches live"
        onClose={() => setPendingShare(null)}
        onConfirm={async (ids, names) => {
          const p = pendingShare;
          setPendingShare(null);
          if (p) await doShare(p.type, p.ref_id, p.payload, ids, names);
        }}
      />
    </AnimatePresence>
  );
}

/* ───────────────────── lists ───────────────────── */
function EmptyState({ msg }) {
  return (
    <div className="text-center py-16 text-white/40 text-sm">
      <Hash size={20} className="mx-auto mb-3 text-white/30" />
      {msg}
    </div>
  );
}

function ListEmpty({ msg }) {
  return <div className="text-xs text-white/40 text-center py-12 border border-dashed border-white/10 rounded-md">{msg}</div>;
}

function AssetRow({ icon: Icon, color, title, meta, badge, onShare, onPreview, sharing, busyKey }) {
  const isBusy = sharing === busyKey;
  return (
    <div className="bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 rounded-md p-3 transition-colors flex items-start gap-3">
      <div className="shrink-0 w-9 h-9 rounded-md flex items-center justify-center" style={{ background: `${color}22`, color }}>
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-white truncate">{title}</div>
        <div className="text-[11px] text-white/50 mt-0.5 flex items-center gap-1.5">
          {badge && <span className="px-1.5 py-0.5 bg-white/10 rounded-sm font-mono uppercase tracking-wider text-[9px]">{badge}</span>}
          <span>{meta}</span>
        </div>
      </div>
      {onPreview && (
        <button
          onClick={onPreview}
          data-testid={`preview-${busyKey}`}
          title="Preview questions & answers (teacher only)"
          className="shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-white/20 text-white/70 text-[12px] font-semibold hover:border-white/50 hover:text-white transition-colors"
        >
          <Eye size={12} /> View
        </button>
      )}
      <button
        onClick={onShare}
        disabled={isBusy}
        data-testid={`share-${busyKey}`}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#FFD600] text-[#0A0C10] text-[12px] font-semibold hover:bg-[#FFC600] transition-colors disabled:opacity-60"
      >
        {isBusy ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} />}
        Share
      </button>
    </div>
  );
}

function ChaptersList({ items, onShare, sharing }) {
  if (!items?.length) return <ListEmpty msg="No chapters yet for this subject." />;
  return (
    <div className="space-y-2">
      {items.map((c) => (
        <AssetRow
          key={c.id}
          icon={BookOpen} color="#0EA5E9"
          title={c.title}
          meta={`${(c.pages || []).length} page${(c.pages || []).length === 1 ? "" : "s"}`}
          onShare={() => onShare(c)}
          sharing={sharing}
          busyKey={`chapter-${c.id}`}
        />
      ))}
    </div>
  );
}

function QuizzesList({ items, onShare, sharing }) {
  const [preview, setPreview] = useState(null);
  if (!items?.length) return <ListEmpty msg="No quizzes yet. Open a Subject → AI Quiz to create one." />;
  return (
    <div className="space-y-2">
      {items.map((q) => (
        <AssetRow
          key={q.id}
          icon={ClipboardList} color="#7C3AED"
          title={q.title}
          badge={q.category || "mixed"}
          meta={`${(q.questions || []).length} question${(q.questions || []).length === 1 ? "" : "s"}`}
          onShare={() => onShare(q)}
          onPreview={() => setPreview(q)}
          sharing={sharing}
          busyKey={`quiz-${q.id}`}
        />
      ))}

      {/* Teacher-only quiz preview — see questions + correct answers without sharing */}
      {preview && (
        <div className="fixed inset-0 z-[95] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="quiz-preview-modal">
          <div className="w-full max-w-2xl max-h-[82vh] overflow-y-auto bg-[#0B0F17] border border-[#7C3AED]/40 rounded-xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#0B0F17] px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#C084FC] font-bold">/ quiz preview · teacher only</div>
                <div className="font-display font-bold text-white text-base mt-0.5">{preview.title}</div>
              </div>
              <button onClick={() => setPreview(null)} data-testid="quiz-preview-close" className="text-white/60 hover:text-white p-1 rounded hover:bg-white/10"><X size={15} /></button>
            </div>
            <div className="p-5 space-y-4">
              {(preview.questions || []).map((qq, i) => {
                const ci = correctIndexOf(qq);
                const correctText = qq.options?.[ci]
                  ?? (qq.answers?.length ? qq.answers.join(", ") : (qq.answer || qq.correct_answer || ""));
                return (
                  <div key={i} className="bg-white/[0.04] border border-white/10 rounded-lg p-4" data-testid={`quiz-preview-q-${i}`}>
                    <div className="text-xs font-mono text-white/40 mb-1">Q{i + 1}</div>
                    <div className="text-sm text-white font-semibold">{qq.question || qq.sentence || qq.q}</div>
                    {qq.options?.length ? (
                      <div className="mt-2 space-y-1">
                        {qq.options.map((o, oi) => (
                          <div key={oi} className={`text-xs px-2.5 py-1.5 rounded ${oi === ci ? "bg-[#10B981]/15 text-[#34D399] border border-[#10B981]/40 font-semibold" : "text-white/60 bg-white/[0.03]"}`}>
                            {oi === ci ? "✓ " : ""}{o}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mt-2 text-xs text-[#34D399]">Answer: {correctText || "—"}</div>
                    )}
                    {qq.explanation && <div className="mt-2 text-[11px] text-white/40">{qq.explanation}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StoriesList({ items, onShare, sharing }) {
  if (!items?.length) return <ListEmpty msg="No animated stories yet." />;
  return (
    <div className="space-y-2">
      {items.map((s) => (
        <AssetRow
          key={s.id}
          icon={ScrollText} color="#F97316"
          title={s.title}
          meta={`${(s.slides || []).length} slides`}
          onShare={() => onShare(s)}
          sharing={sharing}
          busyKey={`story-${s.id}`}
        />
      ))}
    </div>
  );
}

function LibraryList({ items, onShare, sharing }) {
  if (!items?.length) return <ListEmpty msg="No library items yet for this subject." />;
  return (
    <div className="space-y-2">
      {items.map((a) => (
        <AssetRow
          key={a.id}
          icon={FileText} color="#A855F7"
          title={a.title}
          badge={a.kind || "file"}
          meta="Tap to share with class"
          onShare={() => onShare(a)}
          sharing={sharing}
          busyKey={`resource-${a.id}`}
        />
      ))}
    </div>
  );
}

function GamesList({ items, onShare, sharing }) {
  if (!items?.length) return <ListEmpty msg="No Champ Student games yet — generate one from a chapter." />;
  return (
    <div className="space-y-2">
      {items.map((g) => (
        <AssetRow
          key={g.id}
          icon={Crown} color="#FFD600"
          title={g.title}
          badge="$1M ladder"
          meta={`${(g.questions || []).length} question${(g.questions || []).length === 1 ? "" : "s"}`}
          onShare={() => onShare(g)}
          sharing={sharing}
          busyKey={`millionaire-${g.id}`}
        />
      ))}
    </div>
  );
}

function SlideDecksList({ items, onShare, sharing }) {
  if (!items?.length) return <ListEmpty msg="No slide decks yet." />;
  return (
    <div className="space-y-2">
      {items.map((d) => (
        <AssetRow
          key={d.id}
          icon={LayersIcon} color="#0891B2"
          title={d.title}
          badge={d.theme || "indigo"}
          meta={`${(d.slides || []).length} slides`}
          onShare={() => onShare(d)}
          sharing={sharing}
          busyKey={`slides-${d.id}`}
        />
      ))}
    </div>
  );
}

/* ───────────────────── floating launcher button ───────────────────── */
export function LiveSubjectLauncher({ onOpen, hasActiveShare }) {
  return (
    <button
      onClick={onOpen}
      data-testid="subject-toolkit-btn"
      className={`text-xs sm:text-sm font-semibold px-3 sm:px-5 py-2 sm:py-2.5 rounded-full border inline-flex items-center gap-1.5 sm:gap-2 transition-all duration-200 hover:scale-105 ${hasActiveShare
        ? "bg-[#FFD600] text-[#0A0C10] border-transparent shadow-[0_0_18px_rgba(255,214,0,0.5)]"
        : "border-[#FFD600]/40 bg-[#FFD600]/10 text-[#FFD600] hover:text-[#0A0C10] hover:bg-[#FFD600] hover:shadow-[0_0_18px_rgba(255,214,0,0.55)]"}`}
    >
      <Sparkles size={15} /> <span className="hidden sm:inline">Subject toolkit</span><span className="sm:hidden">Toolkit</span>
      {hasActiveShare && <ChevronRight size={12} />}
    </button>
  );
}
